import { locationToApi } from "./msrLocations";
import { LOGIKOR_AUTOMATIC } from "@utils/shipVia";
import {
  calculateFreightClass,
  unitTotals,
} from "../components/handling/handlingUnitFields";

// LWI-1549 — payload mappers between the wizard store and the API.

const HEADER_KEYS = [
  "requestedBy",
  "prepaidOrCollect",
  "isHot",
  "reasonForShipment",
  "msrContainsWipJob",
  "governmentProperty",
  "authorizedName",
  "areasOfOperation",
  "shipmentType",
  "cmrNumber",
  "ciNumber",
  "sinNumber",
  "vcrNumber",
  "receiptNumber",
  "originalPO",
  "vendorRep",
  "customerTaxLICFed",
  "customerTaxLICProv",
  "endUse",
  "otherReference",
  "notes",
  "chargeType",
  "currency",
  "customsValue",
  "transportationAccount",
  "owner",
];

/**
 * PUT msr/{n}/attributes body: the whole header (every header tab) in the
 * nested API shape. Parts are deliberately not included.
 */
export const headerToApi = (details = {}) => {
  const out = {};
  HEADER_KEYS.forEach((k) => {
    if (details[k] !== undefined) out[k] = details[k];
  });
  ["shipFrom", "shipTo", "chargeTo"].forEach((k) => {
    if (details[k]) out[k] = locationToApi(details[k]);
  });
  return out;
};

const flatLocation = (prefix, loc) => {
  const l = locationToApi(loc) || {};
  return {
    [`${prefix}LocationCode`]: l.locationCode ?? "",
    [`${prefix}Name`]: l.name ?? "",
    [`${prefix}Address1`]: l.address1 ?? "",
    [`${prefix}Address2`]: l.address2 ?? "",
    [`${prefix}City`]: l.city ?? "",
    [`${prefix}State`]: l.state ?? "",
    [`${prefix}Zip`]: l.postalCode ?? "",
    [`${prefix}Country`]: l.country ?? "",
    [`${prefix}Open`]: l.openHours ?? "",
    [`${prefix}Close`]: l.closedHours ?? "",
    [`${prefix}Contact`]: l.contactName ?? "",
    [`${prefix}ContactPhone`]: l.phone ?? "",
    [`${prefix}ContactEmail`]: l.email ?? "",
  };
};

/**
 * POST msr/{n} body (CreateMSR): the FLAT shape the Create page posts —
 * every header key present (CreateMSR reads them without a null guard),
 * flattened locations, parts, and the draft flag.
 */
export const toCreatePayload = (
  msrNumber,
  details = {},
  parts = [],
  { isDraft = true } = {}
) => ({
  msrNumber,
  wipJob: details.msrContainsWipJob ?? details.wipJob ?? "",
  governmentProperty: details.governmentProperty ?? "",
  authorizedName: details.authorizedName ?? "",
  areasOfOperation: details.areasOfOperation ?? "",
  shipmentType: details.shipmentType ?? "", // LWI-1594 — blank stays blank
  cmrNumber: details.cmrNumber ?? "",
  receiptNumber: details.receiptNumber ?? "",
  originalPO: details.originalPO ?? "",
  vendorRep: details.vendorRep ?? "",
  ciNumber: details.ciNumber ?? "",
  sinNumber: details.sinNumber ?? "",
  customerTaxLICFed: details.customerTaxLICFed ?? "",
  customerTaxLICProv: details.customerTaxLICProv ?? "",
  endUse: details.endUse ?? "",
  vcrNumber: details.vcrNumber ?? "",
  requestedBy: details.requestedBy ?? "",
  otherReference: details.otherReference ?? "",
  chargeType: details.chargeType ?? "",
  prepaidOrCollect: details.prepaidOrCollect ?? "prepaid",
  currency: details.currency ?? "USD",
  customsValue: details.customsValue ?? "",
  transportationAccount: details.transportationAccount ?? "",
  reasonForShipment: details.reasonForShipment ?? "",
  project: details.project ?? "",
  incoTerms: details.incoTerms ?? "",
  shipmentPriority: details.shipmentPriority ?? "",
  program: details.program ?? "",
  namedPlace: details.namedPlace ?? "",
  notes: details.notes ?? "",
  isHot: details.isHot ? 1 : 0,
  isDraft,
  parts,
  ...flatLocation("shipTo", details.shipTo),
  ...flatLocation("shipFrom", details.shipFrom),
  ...flatLocation("chargeTo", details.chargeTo),
});

/** The Y-m-d part of any date-ish value the pickers / API hand us, or null. */
export const dateOnly = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * LWI-1624 — the PUT msr/pickandpack body. Final Details is no longer a
 * section, so what it collected is derived here on every save: requested
 * pickup date = start of the pickup window, pieces / weight / freight class
 * from the units (Shipped By falls back to the saving user server-side).
 */
export const pickPackPayload = ({ pickAndPack = {}, handlingUnits = [], shipmentId }, extra = {}) => {
  const totals = unitTotals(handlingUnits);
  return {
    ...pickAndPack,
    // The window start wins whenever it parses, so moving Pickup Start after the
    // first save moves the requested date with it; the stored value is only a fallback.
    requestedPickupDate:
      dateOnly(pickAndPack.earlyPickup) || pickAndPack.requestedPickupDate || null,
    totalPieces: totals.pieces,
    totalWeight: Number(totals.weight.toFixed(2)),
    freightClass: calculateFreightClass(handlingUnits),
    shipment_id: shipmentId,
    handlingUnits,
    ...extra,
  };
};

/** GET msr/getpickandpack/{id} row (snake) → the camelCase state the Pick and Pack tabs edit. */
export const pickPackFromApi = (row = {}) => ({
  // Ship Via defaults to the automatic Logikor option when nothing is stored
  // (the classic page did the same, LWI-1145); a saved manual carrier is kept.
  shipVia: String(row.ship_via ?? "").trim() || LOGIKOR_AUTOMATIC,
  shippingMethod: row.shipping_method ?? "",
  containerNumber: row.container_number ?? "",
  hclAssignedContainerNumber: row.hcl_assigned_container_number ?? "",
  transportationAccountNumber: row.transportation_account_number ?? "",
  billingReference: row.billing_reference ?? "",
  earlyPickup: row.early_pickup ?? null,
  latePickup: row.late_pickup ?? null,
  // LWI-1623 — Lima rarely need appointments: an unset value reads "no" (still editable).
  pickupAppointmentRequired: row.pickup_appointment_required || "no",
  earlyDrop: row.early_drop ?? null,
  lateDrop: row.late_drop ?? null,
  dropAppointmentRequired: row.drop_appointment_required || "no",
  flexibleDateTime: row.flexible_date_time ?? "",
  authorizedName: row.authorize_name ?? "",
  requestedPickupDate: row.requested_pickup_date ?? null,
  shippedBy: row.shipped_by ?? "",
  totalPieces: row.total_pieces ?? "",
  totalWeight: row.total_weight ?? "",
  freightClass: row.freight_class ?? "",
  notes: row.notes ?? "",
  bookingNumber: row.booking_number ?? "",
  vesselVoyageNumber: row.voyage_number ?? "",
  additionalCarrier: row.additional_carrier ?? "",
  additionalBOL: row.additional_bol ?? "",
  carrierCode: row.carrier_code ?? "",
  placeOfExit: row.place_of_exit ?? "",
  dateOfExit: row.date_of_exit ?? null,
  dateOfArrival: row.date_of_arrival ?? null,
  sealNumber: row.seal_number ?? "",
  containerTare: row.container_tare ?? "",
  dunnageWeight: row.dunnage_weight ?? "",
});

/** Sum of extended cost (fallback unit × qty) — LWI-1460 Total Value. */
export const totalValueOf = (parts = []) =>
  (Array.isArray(parts) ? parts : []).reduce((sum, p) => {
    const extended = parseFloat(p.extendedCost ?? p.extended_cost);
    if (!Number.isNaN(extended)) return sum + extended;
    const unit = parseFloat(p.unitCost ?? p.unit_cost) || 0;
    const qty = parseFloat(p.quantity ?? p.qty) || 0;
    return sum + unit * qty;
  }, 0);
